'use client';

/**
 * RunbookModeSwitcher - Admin-only pill in the header for instant runbook switching.
 *
 * Shows the active mode abbreviation (e.g. "TPS", "CBSA", "EU") as a compact
 * clickable pill. On click, opens a popover listing all available runbooks.
 * Selecting one calls PUT /api/admin/runbook-mode, then router.refresh() to
 * re-render the RunbookConfig server component instantly.
 *
 * Visible only to platform admins. Other roles see nothing.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { ChevronDown, Check, Loader2 } from 'lucide-react';
import { useIsAdmin } from '@/components/shell/AppShellContext';

const SWITCHER_INVALIDATE_KEY = 'runbook-mode-changed-at';
const SWITCHER_INVALIDATE_WINDOW_MS = 90_000;

// ─── Types ────────────────────────────────────────────────────────────────────

interface RunbookOption {
  id: string;
  name: string;
  dbName: string;
  metadata: Record<string, string>;
  is_active: boolean;
}

interface ActiveRunbook {
  id: string;
  name: string;
  metadata: Record<string, string>;
}

// ─── Abbreviation helper ──────────────────────────────────────────────────────

function getAbbreviation(runbook: { id: string; metadata: Record<string, string> }): string {
  // Prefer the organization abbreviation from metadata
  if (runbook.metadata?.organization_abbreviation) {
    return runbook.metadata.organization_abbreviation;
  }
  // Fall back to uppercased first letters of the id segments
  return runbook.id
    .split('-')
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

// ─── Component ────────────────────────────────────────────────────────────────

export function RunbookModeSwitcher() {
  const isAdmin = useIsAdmin();
  const router = useRouter();

  const [active, setActive] = useState<ActiveRunbook | null>(null);
  const [available, setAvailable] = useState<RunbookOption[]>([]);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Read initial active runbook from injected window config (set by RunbookConfig server component)
  useEffect(() => {
    const injected = window.__RUNBOOK_CONFIG__?.__activeRunbook;
    if (injected) setActive(injected);
  }, []);

  // Fetch available runbooks on mount (admin-only).
  // Endpoint has Cache-Control: private, max-age=60, stale-while-revalidate=300.
  // template.tsx remounts AppShell on every SPA nav, so this effect re-fires
  // each navigation — we ride the browser cache for the GET (0ms repeat cost).
  // BUT: after a PUT, the cached GET would be stale and would overwrite our
  // fresh local state. We use a sessionStorage marker (survives remount) to
  // force a cache-bust for up to 90s after a switch.
  useEffect(() => {
    if (!isAdmin) return;
    let aborted = false;
    // sessionStorage can throw SecurityError under strict ITP / corporate
    // policy. Treat any failure as "no marker" — fall through to cached fetch.
    let changedAt = 0;
    try {
      if (typeof window !== 'undefined') {
        changedAt = parseInt(sessionStorage.getItem(SWITCHER_INVALIDATE_KEY) || '0', 10);
      }
    } catch { /* sessionStorage blocked — proceed with default cache */ }
    const recentlyChanged = Date.now() - changedAt < SWITCHER_INVALIDATE_WINDOW_MS;
    const init: RequestInit = recentlyChanged ? { cache: 'reload' } : {};
    fetch('/api/admin/runbook-mode', init)
      .then((r) => r.json())
      .then((data) => {
        if (aborted) return;
        if (data.available) setAvailable(data.available);
        if (data.active) setActive(data.active);
        // Once we've seen fresh data, drop the invalidation marker so the
        // next mount (within max-age) can use the cache normally.
        if (recentlyChanged && typeof window !== 'undefined') {
          try { sessionStorage.removeItem(SWITCHER_INVALIDATE_KEY); } catch {}
        }
      })
      .catch(() => {/* silent — non-critical */});
    return () => { aborted = true; };
  }, [isAdmin]);

  // Close on click outside
  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  if (!isAdmin) return null;
  if (!active && available.length === 0) return null;

  const abbreviation = active ? getAbbreviation(active) : '…';

  async function switchTo(dbName: string) {
    if (switching) return;
    setSwitching(true);
    setOpen(false);
    try {
      const res = await fetch('/api/admin/runbook-mode', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: dbName }),
      });
      const data = await res.json();
      if (data.success && data.active) {
        setActive(data.active);
        // Update is_active flags in the available list so the dropdown highlights correctly
        setAvailable((prev) =>
          prev.map((rb) => ({ ...rb, is_active: rb.dbName === dbName }))
        );
        // Marker survives template-driven remount; the mount effect uses it
        // to cache-bust the next GET so we don't read a pre-switch response
        // from the browser cache and overwrite local state with stale data.
        if (typeof window !== 'undefined') {
          try {
            sessionStorage.setItem(SWITCHER_INVALIDATE_KEY, String(Date.now()));
          } catch {/* sessionStorage blocked — fall back to 60s cache window */}
        }
      }
    } catch {
      // Silent — the poll will self-correct within 15s
    } finally {
      // Re-enable the pill before triggering refresh — router.refresh() can suspend
      // the component tree while waiting for the server response, which would leave
      // the pill stuck in disabled/spinner state if setSwitching ran after.
      setSwitching(false);
    }
    // Trigger full server component re-render AFTER local state is settled.
    // This runs outside try/finally so the pill is already interactive.
    router.refresh();
  }

  return (
    <div ref={popoverRef} className="relative">
      {/* Pill button */}
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={switching}
        title={active?.name ?? 'Switch runbook mode'}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border transition-all duration-150
          bg-indigo-500/10 border-indigo-500/30 text-indigo-300
          hover:bg-indigo-500/20 hover:border-indigo-400/50
          disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {switching ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : null}
        <span>{abbreviation}</span>
        <ChevronDown className={`w-3 h-3 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Popover */}
      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-50 min-w-[220px] rounded-lg border border-slate-700 bg-slate-900 shadow-xl overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-800">
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Runbook Mode</p>
          </div>
          <ul className="py-1">
            {available.map((rb) => {
              const abbr = getAbbreviation(rb);
              const isActive = rb.is_active;
              return (
                <li key={rb.dbName}>
                  <button
                    onClick={() => !isActive && switchTo(rb.dbName)}
                    disabled={isActive || switching}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors
                      ${isActive
                        ? 'bg-indigo-500/10 cursor-default'
                        : 'hover:bg-slate-800 cursor-pointer'}
                    `}
                  >
                    {/* Abbreviation badge */}
                    <span className={`flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center text-xs font-bold
                      ${isActive
                        ? 'bg-indigo-500/20 text-indigo-300'
                        : 'bg-slate-700 text-slate-400'}`}
                    >
                      {abbr}
                    </span>

                    {/* Labels */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${isActive ? 'text-white' : 'text-slate-300'}`}>
                        {rb.name}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {rb.metadata?.organization ?? rb.dbName}
                      </p>
                    </div>

                    {/* Active checkmark */}
                    {isActive && <Check className="w-4 h-4 text-indigo-400 flex-shrink-0" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
